import { useState, useEffect, useCallback, useRef } from 'react';
import { Save, Loader2, Image } from 'lucide-react';

import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { useAutoSave } from '../../hooks/useAutoSave';
import { adminFetch, getAuthHeaders } from '../../services/adminApi';

import { PageTitle } from './shared';
import { ImagePicker } from './shared';

const HeroEditor = () => {
  const [heroData, setHeroData] = useState({
    title_line1: '',
    title_line2: '',
    subtitle: '',
    media_type: 'image',
    hero_image_url: '',
    hero_video_url: ''
  });
  const [loading, setLoading] = useState(false);
  const heroDataRef = useRef(heroData);

  // Keep ref updated
  useEffect(() => {
    heroDataRef.current = heroData;
  }, [heroData]);

  const performSave = useCallback(async () => {
    const response = await adminFetch(`${process.env.REACT_APP_BACKEND_URL}/api/hero-content`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(heroDataRef.current)
    });

    if (!response.ok) {
      throw new Error('Failed to save');
    }
  }, []);

  const { isDirty, isSaving, scheduleAutosave, saveNow, markClean } = useAutoSave(performSave, 5000);

  useEffect(() => {
    fetchHeroData();
  }, []);

  const fetchHeroData = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/hero-content`);
      if (response.ok) {
        const data = await response.json();
        setHeroData(data);
      }
    } catch (error) {
      console.error('Failed to load hero data');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setHeroData(prev => ({ ...prev, [field]: value }));
    scheduleAutosave();
  };

  // Convert YouTube URL to embed format
  const convertYouTubeUrl = (url) => {
    if (!url) return '';

    // Already in embed format
    if (url.includes('youtube.com/embed/')) {
      return url;
    }

    // Extract video ID from various YouTube URL formats
    let videoId = '';

    // Format: https://www.youtube.com/watch?v=VIDEO_ID
    if (url.includes('youtube.com/watch?v=')) {
      videoId = url.split('watch?v=')[1]?.split('&')[0];
    }
    // Format: https://youtu.be/VIDEO_ID
    else if (url.includes('youtu.be/')) {
      videoId = url.split('youtu.be/')[1]?.split('?')[0];
    }
    // Format: https://www.youtube.com/v/VIDEO_ID
    else if (url.includes('youtube.com/v/')) {
      videoId = url.split('/v/')[1]?.split('?')[0];
    }

    // Convert to embed format if video ID found
    if (videoId) {
      return `https://www.youtube.com/embed/${videoId}`;
    }

    return url;
  };

  const handleVideoUrlChange = (url) => {
    const embedUrl = convertYouTubeUrl(url);
    handleChange('hero_video_url', embedUrl);
  };

  if (loading) {
    return <div className="flex justify-center p-8">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      <PageTitle
        title="Hero Section"
        description="Update the main hero section content and image"
        icon={<Image className="h-6 w-6" />}
        actions={
          <Button
            onClick={saveNow}
            disabled={!isDirty || isSaving}
            className="admin-btn-primary"
          >
            {isSaving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
            {isSaving ? 'Saving...' : 'Save Now'}
          </Button>
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <div>
            <Label htmlFor="title_line1" className="text-gray-800 dark:text-gray-200 font-medium">Title Line 1</Label>
            <Input
              id="title_line1"
              value={heroData.title_line1}
              onChange={(e) => handleChange('title_line1', e.target.value)}
              placeholder="Master Your"
              className="border-stone-300 focus:border-amber-500 focus:ring-amber-500/20 bg-background"
            />
          </div>

          <div>
            <Label htmlFor="title_line2" className="text-gray-800 dark:text-gray-200 font-medium">Title Line 2</Label>
            <Input
              id="title_line2"
              value={heroData.title_line2}
              onChange={(e) => handleChange('title_line2', e.target.value)}
              placeholder="Inner Warrior"
              className="border-stone-300 focus:border-amber-500 focus:ring-amber-500/20 bg-background"
            />
          </div>

          <div>
            <Label htmlFor="subtitle" className="text-gray-800 dark:text-gray-200 font-medium">Subtitle</Label>
            <Textarea
              id="subtitle"
              value={heroData.subtitle}
              onChange={(e) => handleChange('subtitle', e.target.value)}
              placeholder="Expert martial arts training..."
              rows={3}
              className="border-stone-300 focus:border-amber-500 focus:ring-amber-500/20 bg-background"
            />
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <Label htmlFor="media_type" className="text-gray-800 dark:text-gray-200 font-medium">Hero Media Type</Label>
            <Select
              value={heroData.media_type || 'image'}
              onValueChange={(value) => handleChange('media_type', value)}
            >
              <SelectTrigger id="media_type" className="border-stone-300 focus:border-amber-500 focus:ring-amber-500/20 bg-background">
                <SelectValue placeholder="Select media type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="image">Image</SelectItem>
                <SelectItem value="video">Video (YouTube)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {heroData.media_type === 'image' ? (
            <ImagePicker
              value={heroData.hero_image_url}
              onChange={(url) => handleChange('hero_image_url', url)}
              context="hero"
              label="Hero Image"
              previewSize="lg"
            />
          ) : (
            <div className="space-y-4">
              <div>
                <Label htmlFor="hero_video_url" className="text-gray-800 dark:text-gray-200 font-medium">YouTube Video URL</Label>
                <Input
                  id="hero_video_url"
                  value={heroData.hero_video_url || ''}
                  onChange={(e) => handleVideoUrlChange(e.target.value)}
                  placeholder="https://www.youtube.com/watch?v=VIDEO_ID or https://youtu.be/VIDEO_ID"
                  className="border-stone-300 focus:border-amber-500 focus:ring-amber-500/20 bg-background"
                />
                <p className="text-xs text-stone-500 dark:text-stone-400 mt-1">
                  Enter any YouTube URL format - it will be automatically converted to embed format.
                </p>
                <p className="text-xs text-amber-700 mt-1">
                  Supported formats: youtube.com/watch?v=, youtu.be/, youtube.com/v/
                </p>
              </div>

              {heroData.hero_video_url && (
                <div className="mt-2">
                  <Label className="text-gray-800 dark:text-gray-200 font-medium">Preview</Label>
                  <div className="aspect-video mt-2 bg-black rounded-lg overflow-hidden border-2 border-stone-200">
                    <iframe
                      src={heroData.hero_video_url}
                      className="w-full h-full"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      title="Hero Video Preview"
                    ></iframe>
                  </div>
                </div>
              )}

              <div>
                <Label htmlFor="hero_image_url" className="text-gray-800 dark:text-gray-200 font-medium">Video Thumbnail (Optional)</Label>
                <Input
                  id="hero_image_url"
                  value={heroData.hero_image_url || ''}
                  onChange={(e) => handleChange('hero_image_url', e.target.value)}
                  placeholder="https://example.com/thumbnail.jpg"
                  className="border-stone-300 focus:border-amber-500 focus:ring-amber-500/20 bg-background"
                />
                <p className="text-xs text-stone-500 dark:text-stone-400 mt-1">
                  Optional: Provide a thumbnail image for loading states
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="flex justify-end items-center gap-3 pt-4 border-t border-stone-200">
        {isSaving && (
          <span className="text-sm text-amber-700 flex items-center">
            <Loader2 className="h-4 w-4 mr-1 animate-spin" />
            Saving...
          </span>
        )}
        {isDirty && !isSaving && (
          <Button
            onClick={saveNow}
            variant="adminGold"
          >
            <Save className="h-4 w-4 mr-2" />
            Save Now
          </Button>
        )}
      </div>
    </div>
  );
};

export default HeroEditor;